import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Observable, forkJoin, throwError } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { JsonMapperService } from 'src/app/core/services/mapper/mapper.service';
import { Operateur } from '../../models/operateur.model';
import { DomaineSpec } from '../../models/domaine-spec.model';
import { OperateurInformationsDomaine } from '../../models/operateur-informations-domaine.model';
import { ReferencesService } from '../references/references.service';
import { ResponseDrevManquantes } from '../../interfaces/response-drev-manquantes.interface';
import { OperateurExport } from 'src/app/data/edition/models/operateur-export.model';
import { IntervenantCategorie } from '../../models/intervenant-categorie.model';
import { Pays } from '../../models/pays.model';
import { OperateursInformationsDomaine } from '../../models/operateurs-informations.domaine';
import { IntervenantVinMini } from '../../models/intervenant-vin-mini.model';
import { InformationMajeure } from '../../models/information-majeure.model';
import { ModificationOperateur } from '../../models/modification-operateur.model';
import { InformationMineure } from '../../models/information-mineure.model';
import { HistoriqueModification } from '../../models/historique-modification.model';

interface ObjectWithOperateur {
    idOperateur: number;
    operateur: Operateur;
}

interface ObjectWithCvi {
    idOperateur;
    cvi: string;
}

class Omit<T, U> {}

@Injectable({
    providedIn: 'root'
})
export class OperateursService {
    /**
     * Signal sur l'idOperateur
     */
    idOperateur = signal<number>(null);

    constructor(protected readonly http: HttpClient, protected readonly mapper: JsonMapperService, private readonly referencesService: ReferencesService) {}

    public getOperateursAffilies(search: string): Observable<Operateur[]> {
        return forkJoin(this.http.get<Operateur[]>(`/api/intervenant/private/operateurs?search=${search}`), this.referencesService.getReferences()).pipe(
            map(([operateurs, ref]) => this.mapper.deserializeArray(operateurs, Operateur, ref))
        );
    }

    public getNombreOperateursAffilies(): Observable<number> {
        return this.http.get<number>('/api/intervenant/private/operateurs/count');
    }

    public getAutresOperateurs(spec: DomaineSpec, search: string): Observable<Operateur[]> {
        return forkJoin(
            this.http.get<object[]>(`/api/intervenant/private/operateurs?spec=${spec.code}&search=${search}`),
            this.referencesService.getReferences()
        ).pipe(map(([operateur, ref]) => this.mapper.deserializeArray(operateur, Operateur, ref)));
    }

    private handleError(error: HttpErrorResponse) {
        let errorMessage = 'An unknown error occurred!';
        if (error.error instanceof ErrorEvent) {
            // Client-side error
            errorMessage = `Error: ${error.error.message}`;
        } else {
            // Server-side error
            if (error.status === 400 && error.error) {
                errorMessage = `Error: ${error.error.message}`;
            }
        }
        alert(errorMessage); // or use a more sophisticated notification system
        return throwError(errorMessage);
    }

    /**
     * Méthode pour créer un opérateur (afin de ne plus passer par AgriMaker)
     */
    createOperateur(operateur: any): Observable<any> {
        return this.http.post<any>('/api/intervenant/private/operateur', operateur).pipe(catchError(this.handleError));
    }

    public getOperateur(idOperateur: number): Observable<Operateur> {
        return forkJoin(this.http.get<object>(`/api/intervenant/private/details-operateurs/${idOperateur}`), this.referencesService.getReferences()).pipe(
            map(([operateur, ref]) => this.mapper.deserializeObject(operateur, Operateur, ref))
        );
    }

    /**
     * Récupère les informations des intervenants via une liste d'ids
     * @param idsOperateur - Liste d'ids opérateur
     */
    getOperateurs(idsOperateur: number[]): Observable<Operateur[]> {
        return this.http.post<Operateur[]>('/api/intervenant/private/details-operateurs', idsOperateur).pipe(
            map(infos => {
                return this.mapper.deserializeArray(infos, Operateur);
            })
        );
    }

    public getOperateursByOrganisme(idOrganisme: number): Observable<OperateurExport[]> {
        return forkJoin(this.http.get<object[]>(`/api/intervenant/private/organismes/${idOrganisme}/operateurs`), this.referencesService.getReferences()).pipe(
            map(([operateur, ref]) => this.mapper.deserializeArray(operateur, OperateurExport, ref))
        );
    }

    public getOperateursById(idOrganisme: number, idIntervenants: number[]): Observable<OperateurExport[]> {
        return forkJoin(
            this.http.post<object[]>(`/api/intervenant/private/organismes/${idOrganisme}/operateurs`, idIntervenants),
            this.referencesService.getReferences()
        ).pipe(map(([operateur, ref]) => this.mapper.deserializeArray(operateur, OperateurExport, ref)));
    }

    public getOperateurPipe<T extends ObjectWithOperateur | ObjectWithOperateur[]>() {
        return tap((list: T[]) => {
            const liste: ObjectWithOperateur[] = [].concat(list);

            liste.forEach(item => {
                this.getOperateur(item.idOperateur).subscribe(operateur => (item.operateur = operateur));
            });
        });
    }

    /**
     * Récupère toutes les informations Domaines pour le formulaire de création d'un opérateur
     */
    public getAllInformationsDomaine(): Observable<DomaineSpec[]> {
        return this.http.get<object[]>('/api/intervenant/private/domaines_specs').pipe(
            map(infos => {
                return this.mapper.deserializeArray(infos, DomaineSpec);
            })
        );
    }

    /**
     * Récupère tous les pays pour le formulaire de modifications et de création d'un opérateur
     */
    public getPays(): Observable<Pays[]> {
        return this.http.get<object[]>('/api/intervenant/private/pays').pipe(
            map(infos => {
                const result = this.mapper.deserializeArray(infos, Pays);
                return result;
            })
        );
    }

    /**
     * Récupère les informations domaine d'un opérateur
     * @param idOperateur l'Id de l'opérateur
     */
    public getInformationsDomaine(idOperateur: number): Observable<OperateurInformationsDomaine[]> {
        return this.http
            .get<object[]>(`/api/intervenant/private/details-operateurs/${idOperateur}/informations-domaine`)
            .pipe(map(infos => this.mapper.deserializeArray(infos, OperateurInformationsDomaine)));
    }

    /**
     * Récupère les informations domaine de plusieurs opérateurs
     * @param operateurs - Liste d'opérateurs
     */
    public getInformationsDomaineByIdsOperateur(operateurs: Operateur[]): Observable<OperateursInformationsDomaine[]> {
        const idsOperateur = operateurs.map(operateur => operateur.id);
        return this.http
            .post<OperateursInformationsDomaine[]>('/api/intervenant/private/details-operateurs/informations-domaine', idsOperateur)
            .pipe(map(infos => this.mapper.deserializeArray(infos, OperateursInformationsDomaine)));
    }

    /**
     * Mets à jour les informations majeures d'un opérateur via le EditInformationsMajeures.component
     * Les informations sont visibles dans informations-operateur.component
     */
    public updateInformationsMajeures(idOperateur: number, data: InformationMajeure): Observable<void> {
        return this.http.put<void>(`/api/intervenant/private/details-operateurs/${idOperateur}/informations-majeures`, data).pipe(catchError(this.handleError));
    }

    /**
     * Mets à jour les informations mineures d'un opérateur via le EditInformationMineures.component
     * Les informations sont visibles dans informations-operateur.component
     */
    public updateInformationsMineures(idOperateur: number, data: InformationMineure): Observable<void> {
        return this.http.put<void>(`/api/intervenant/private/details-operateurs/${idOperateur}/informations-mineures`, data).pipe(catchError(this.handleError));
    }

    /** Retourne le CVI d'un objet qui contient un ID d'opérateur */
    public getCviPipe<T extends ObjectWithCvi | ObjectWithCvi[]>() {
        return tap((list: T[]) => {
            const liste: ObjectWithCvi[] = [].concat(list);

            liste.forEach(item => {
                this.getInformationsDomaine(item.idOperateur).subscribe(infos => {
                    const cvi = infos.find(info => info.code === 'VIN_CVI');
                    item.cvi = cvi ? cvi.valeur : '';
                });
            });
        });
    }

    /**
     * Récupère la liste des drev manquantes pour un organisme
     * @param idOrganisme - L'id de l'organisme
     */
    public getOperateursDREVManquantes(idOrganisme: number): Observable<ResponseDrevManquantes> {
        return this.http.get<ResponseDrevManquantes>(`/api/declaration/private/drev/${idOrganisme}/manquantes`);
    }

    public countOperateursDREVManquantes(idOrganisme: number): Observable<number> {
        return this.http.get<number>(`/api/declaration/private/drev/${idOrganisme}/manquantes/count`);
    }

    public getOperateursFacturePanel(idOperateur: number, idOrganisme: number): Observable<string> {
        return this.http.get<string>(`/api/facturation/private/organismes/${idOrganisme}/operateurs/${idOperateur}/panel`, { responseType: 'text' as 'json' });
    }

    public getIntervenantCategorie(idIntervenant: number, idOrganisme: number): Observable<IntervenantCategorie> {
        // eslint-disable-next-line max-len
        return this.http.get<IntervenantCategorie>(`/api/intervenant/private/details-operateur/categorie/${idIntervenant}/organisme/${idOrganisme}`).pipe(
            map(categ => this.mapper.deserializeObject(categ, IntervenantCategorie))
            //   catchError(error => {
            //     if (error.status === 404) {
            //       return of(null); // Cas où il n'y a pas de données pour cet ID
            //     }
            //     return throwError(() => new Error('Erreur lors de la récupération des données'));
            //   })
        );
    }

    public addIntervenantCategorie(intervenantCategorie: IntervenantCategorie): Observable<IntervenantCategorie> {
        return this.http.post<IntervenantCategorie>('/api/intervenant/private/details-operateur/categorie', intervenantCategorie);
    }

    public updateIntervenantCategorie(idIntervenantCategorie: number, data: IntervenantCategorie): Observable<IntervenantCategorie> {
        return this.http.put<IntervenantCategorie>(`/api/intervenant/private/details-operateur/categorie/${idIntervenantCategorie}`, data);
    }

    /**
     * Récupère les informations mini de plusieurs intervenants vin
     * @param idsOperateur - Liste d'ids opérateurs
     */
    getIntervenantsVinMini(idsOperateur: number[]): Observable<IntervenantVinMini[]> {
        return this.http.post<IntervenantVinMini[]>('/api/intervenant/private/intervenants/vin-mini', idsOperateur);
    }

    /**
     * Setter pour l'idOperateur
     * @param idOperateur - L'id operateur
     */
    setIdOperateur(idOperateur: number) {
        this.idOperateur.set(idOperateur);
    }

    /**
     * Crée un historique de modification
     * @param data - ModificationMajeure | ModificationMineure
     */
    public addModification(data: ModificationOperateur): Observable<void> {
        return this.http.post<void>(`/api/intervenant/private/details-operateurs/modifications`, data).pipe(catchError(this.handleError));
    }

    /**
     * Récupère toutes les modifications de l'intervenant
     * @param idIntervenant - L'id de l'intervenant
     */
    public getAllModifications(idIntervenant: number): Observable<ModificationOperateur[]> {
        return this.http
            .get<ModificationOperateur[]>(`/api/intervenant/private/details-operateurs/${idIntervenant}/modifications`)
            .pipe(catchError(this.handleError));
    }

    /**
     * Récupère toutes les modifications de l'opérateur
     * @param idOperateur - L'id de l'opérateur
     */
    public getAllModificationsOperateur(idOperateur: number): Observable<ModificationOperateur[]> {
        return this.http
            .get<ModificationOperateur[]>(`/api/intervenant/private/details-operateurs/${idOperateur}/modifications-operateur`)
            .pipe(catchError(this.handleError));
    }

    /**
     * Récupère les modifications pour plusieurs opérateurs et groupées par opérateur
     * @param idsOperateur - Liste d'ids opérateurs
     */
    public getModificationsOperateursGrouped(idsOperateur: number[]): Observable<ModificationOperateur[]> {
        return this.http
            .post<ModificationOperateur[]>('/api/intervenant/private/details-operateurs/modifications-operateurs-grouped', idsOperateur)
            .pipe(catchError(this.handleError));
    }

    /**
     * Récupère les modifications pour plusieurs opérateurs
     * @param idsOperateur - Liste d'ids opérateurs
     */
    public getModificationsOperateurs(idsOperateur: number[]): Observable<ModificationOperateur[]> {
        return this.http
            .post<ModificationOperateur[]>('/api/intervenant/private/details-operateurs/modifications-operateurs', idsOperateur)
            .pipe(catchError(this.handleError));
    }

    /**
     * Récupère l'historique des modifications des opérateurs lié à l'organisme connecté
     * @param idIntervenants - Liste d'id des opérateurs
     * @param dateDebut - Date début de modification
     * @param dateFin - Date fin de modification
     */
    public getHistoriqueModifications(idIntervenants: number[], dateDebut: string, dateFin: string): Observable<HistoriqueModification[]> {
        let params = new HttpParams();
        if (dateDebut) {
            params = params.append('dateDebut', dateDebut);
        }
        if (dateFin) {
            params = params.append('dateFin', dateFin);
        }
        return this.http
            .post<HistoriqueModification[]>('/api/intervenant/private/details-operateurs/historique-modifications', idIntervenants, { params })
            .pipe(catchError(this.handleError));
    }

    /**
     * Récupère l'historique des modifications de l'opérateur
     * @param idOperateur - L'id de l'opérateur
     * @param dateDebut - Date début de modification
     * @param dateFin - Date fin de modification
     */
    public getHistoriqueModificationsOperateur(idOperateur: number, dateDebut: string, dateFin: string): Observable<HistoriqueModification[]> {
        let params = new HttpParams();
        if (dateDebut) {
            params = params.append('dateDebut', dateDebut);
        }
        if (dateFin) {
            params = params.append('dateFin', dateFin);
        }
        return this.http
            .get<HistoriqueModification[]>(`/api/intervenant/private/details-operateurs/${idOperateur}/historique-modifications-operateur`, { params })
            .pipe(catchError(this.handleError));
    }
}
